import { useEffect, useRef } from "react";
import { AudioSettings } from "./AudioSettings";
import type { AudioControl } from "./use-game-audio";
import { FullscreenButton } from "./FullscreenButton";
import "./help.css";

export function HelpPanel({
  audio,
  onClose,
  onFeedback,
  showFullscreen = false,
}: {
  audio: AudioControl;
  onClose: () => void;
  onFeedback?: () => void;
  showFullscreen?: boolean;
}) {
  const element = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = element.current;
    if (!dialog) return;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  return (
    <dialog
      ref={element}
      className="help-dialog"
      aria-labelledby="help-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="help-content">
        <header>
          <h1 id="help-title">Помощь</h1>
          <button type="button" aria-label="Закрыть помощь" onClick={onClose}>
            ×
          </button>
        </header>
        <section aria-label="Управление кораблём">
          <h2>Управление кораблём</h2>
          <p>
            Нажми на свободное место — корабль долетит до точки. Удерживай левую кнопку
            мыши — веди за курсором; после отпускания он долетит до последней точки.
            Нажми на противника — выберешь цель для автоогня.
            WASD и стрелки — дополнительный способ движения.
          </p>
          <p>
            На телефоне левый джойстик — полёт, касание противника — выбор цели.
            Радар или касание свободного места вне джойстика — автопилот.
            Удержание мыши / джойстик / WASD / Esc — отмена курса.
            Space — форсаж · Q / 1 — первый модуль · 2–9, 0, − — следующие · E — взаимодействие.
            R — батарея · F — ракета · T — ремонтный дрон.
          </p>
        </section>
        <section aria-label="Первые вылеты">
          <h2>Первые вылеты</h2>
          <p>
            Пройди обучение и выбери фракцию одним из трёх порталов. Он отправит
            тебя на домашнюю станцию; вернуться в обучение нельзя.
          </p>
          <p>
            Добывай руду из астероидов, собирай обломки и выполняй контракты. В
            доке своей или нейтральной станции сдавай груз и улучшай корабль.
            Домашняя область свободна от обычных врагов, за вратами начинается
            опасный космос.
          </p>
          <p>
            Колесо мыши меняет масштаб. Карта показывает области, врата и
            станции; выбери место, чтобы проложить курс. Трюм раскрывается
            отдельной кнопкой.
          </p>
        </section>
        <section aria-label="Груз и восстановление">
          <h2>Груз и восстановление</h2>
          <p>
            При любой гибели весь трюм выпадает в контейнерах, даже в защищённой
            области. Их может подобрать любой пилот; через 10 минут они исчезнут.
            Купленные корабли и установленное оснащение не теряются.
          </p>
          <p>
            Корабль восстанавливается у ближайшей своей или нейтральной станции,
            с пустым трюмом. Во время обучения — у учебной станции.
          </p>
        </section>
        <AudioSettings audio={audio} />
        {onFeedback && (
          <section aria-label="Обратная связь">
            <h2>Есть идея или ошибка?</h2>
            <button type="button" onClick={onFeedback}>
              Обратная связь
            </button>
          </section>
        )}
        {showFullscreen && (
          <section aria-label="Экран">
            <h2>Полный экран</h2>
            <FullscreenButton />
          </section>
        )}
      </div>
    </dialog>
  );
}
